import { handleAdLink } from "@/utils/handleAdLink";
import imageUrl from "@/utils/imageUrl";
import { Carousel, Dropdown } from "antd";
import classNames from "classnames";
import Head from "next/head";
import Link from "next/link";
import { useCallback, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { EVENT } from "../../constants/event";
import setCloseAds from "../../utils/closeAds";
import AiTag from "../AiTag";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";

function RecommendCarouselCard({ data, record, onClose, columnSize = 240 }) {
  const [index, setIndex] = useState(0);
  const carouselRef = useRef(null);
  const mergeCol = record.mergeCol || 1;
  const displayWidth = columnSize * mergeCol + 16 * (mergeCol - 1);

  const onAfterChange = useCallback((index) => {
    setIndex(index);
  }, []);

  const onNext = useCallback(() => {
    carouselRef.current?.next();
  }, []);

  const onPrev = useCallback(() => {
    carouselRef.current?.prev();
  }, []);

  const list = useMemo(() => {
    return data?.list?.map((item, index) => (
      <Link
        href={item?.link}
        key={index}
        target={item?.link?.startsWith("http") ? "_blank" : "_self"}
        rel={"noreferrer nofollow"}
        data-gd-click="resource_loc_click"
        data-gd-expose="resource_loc_expose"
        data-expose-time={mergeCol > 1 ? 1 : null}
        data-resource-type={item.type}
        data-resource-id={record?.material?.id}
        data-resource-title={record?.promotion?.name}
        data-content-source={record?.position?.alias}
        data-click-type="点击广告"
        data-aigc
      >
        <If condition={item.type === "video"}>
          <Then>
            <video
              src={item.src}
              loop
              autoPlay
              preload="metadata"
              muted
              {...(item.poster ? { poster: item.poster } : null)}
            ></video>
          </Then>
          <Else>
            <img src={item.src} alt={data?.title} />
          </Else>
        </If>
        <AiTag pin={item} size="md" />
      </Link>
    ));
  }, [data, mergeCol, record]);

  const menu = useMemo(() => {
    return (
      <div className={styles.menu}>
        <a
          className={styles.menuItem}
          onClick={onClose}
          data-gd-click="resource_loc_click"
          data-click-type="关闭广告"
        >
          <span className={styles.text}>关闭广告</span>
        </a>
      </div>
    );
  }, [onClose]);

  return (
    <div
      className={classNames(styles.carousel, {
        [styles.mergeCol]: mergeCol > 1,
      })}
    >
      <div
        className={styles.container}
        style={{
          width: displayWidth,
          height: `${(data?.height / data?.width) * displayWidth}px`,
        }}
      >
        <When condition={data?.list?.length > 1}>
          <button
            className={styles.prev}
            disabled={index <= 0}
            onClick={onPrev}
          >
            <HBIcon type="ic_chevron_left" />
          </button>
        </When>
        <If condition={data?.list?.length > 1}>
          <Then>
            <Carousel
              ref={carouselRef}
              dots={{ className: styles.dot }}
              autoplay
              afterChange={onAfterChange}
            >
              {list}
            </Carousel>
          </Then>
          <Else>{list}</Else>
        </If>
        <When condition={data?.list?.length > 1}>
          <button
            className={styles.next}
            disabled={index >= data?.list?.length - 1}
            onClick={onNext}
          >
            <HBIcon type="ic_chevron_right" />
          </button>
        </When>
        <When condition={mergeCol > 1 && record.showcase.closable}>
          <div
            className={styles.closeIcon}
            onClick={onClose}
            data-gd-click="resource_loc_click"
            data-click-type="关闭广告"
          >
            <HBIcon type="ic_close" />
          </div>
        </When>
        <When condition={mergeCol > 1 && record.material.tag}>
          <div className={styles.labelTag}>{record.material.tag}</div>
        </When>
      </div>
      <When condition={data?.title}>
        <div className={styles.title} title={data?.title}>
          {data?.title}
        </div>
      </When>
      <div className={styles.desc}>
        <div className={styles.left}>
          <When condition={data?.logo}>
            <div className={styles.logo}>
              <img src={data?.logo} alt={data?.name} />
            </div>
          </When>
          <When condition={data?.name}>
            <div className={styles.name}>{data?.name}</div>
          </When>
          <When condition={mergeCol === 1 && record.material.tag}>
            <div className={styles.label}>
              <div className={styles.text}>{record.material.tag}</div>
            </div>
          </When>
        </div>
        <When condition={mergeCol === 1 && record.showcase.closable}>
          <div className={styles.right}>
            <Dropdown overlay={menu} placement="bottomRight">
              <div className={styles.close}>
                <HBIcon type="ic_more" />
              </div>
            </Dropdown>
          </div>
        </When>
      </div>
      <When condition={record.material.tracker}>
        <img
          src={record.material.tracker}
          alt="埋点"
          style={{ display: "none" }}
        />
      </When>
    </div>
  );
}

export default function RecommendCard({
  record,
  style,
  onDelete,
  columnSize = 240,
}) {
  const onClose = useCallback(
    (evt) => {
      evt.preventDefault();
      const pinId = record.pin_id;
      const boardId = record.board_id;
      // 判断是否是采集还是画板
      if (pinId) {
        window.dispatchEvent(
          new CustomEvent(EVENT.DELETE_PIN, { detail: pinId })
        );
      } else if (boardId) {
        onDelete(boardId);
      }

      setCloseAds(record.material);
    },
    [record, onDelete]
  );

  const link = useMemo(() => {
    return handleAdLink(record);
  }, [record]);

  if (record.material.type === "html") {
    try {
      const data = JSON.parse(record.material.data);
      if (data.type === "carousel") {
        return (
          <RecommendCarouselCard
            data={data}
            record={record}
            onClose={onClose}
          />
        );
      }

      return null;
    } catch (err) {
      return (
        <div className={styles.html}>
          <div
            dangerouslySetInnerHTML={{
              __html: record.material.data,
            }}
            data-gd-click="resource_loc_click"
            data-gd-expose="resource_loc_expose"
            data-resource-type="html"
            data-resource-id={record?.material?.id}
            data-resource-title={record?.promotion?.name}
            data-content-source={record?.position?.alias}
          ></div>
          <When condition={record.showcase.closable}>
            <div
              className={styles.close}
              onClick={onClose}
              data-gd-click="resource_loc_click"
              data-click-type="关闭广告"
            >
              <HBIcon type="ic_close" />
            </div>
          </When>
          <When condition={record.material.tracker}>
            <img
              src={record.material.tracker}
              alt="埋点"
              style={{ display: "none" }}
            />
          </When>
        </div>
      );
    }
  } else if (record.material.type === "image") {
    const image = record.material.data;
    const fileData = {
      bucket: image.bucket_name,
      key: image.bucket_key,
      width: image.metadata.width,
      height: image.metadata.height,
    };

    const mergeCol = record.mergeCol || 1;
    const displayWidth = columnSize * mergeCol + 16 * (mergeCol - 1);

    const imgUrl =
      typeof image === "string"
        ? { src: image }
        : {
            src: imageUrl(fileData, { width: displayWidth, webp: false }),
            srcSet:
              imageUrl(fileData, { width: displayWidth * 2, webp: false }) +
              " 2x",
          };

    return (
      <>
        <Head>
          {/* 1x 屏幕加载普通图片 */}
          <link
            rel="preload"
            as="image"
            href={imgUrl.src}
            media="(max-resolution: 1.5dppx), (-webkit-max-device-pixel-ratio: 1.5)"
          />
          {/* 2x 屏幕加载高清图片 */}
          {imgUrl.srcSet && (
            <link
              rel="preload"
              as="image"
              href={imgUrl.srcSet.split(" ")[0]}
              media="(min-resolution: 1.51dppx), (-webkit-min-device-pixel-ratio: 1.51)"
            />
          )}
        </Head>

        <Link
          href={link}
          className={styles.img}
          target={link?.startsWith("http") ? "_blank" : "_self"}
          style={{
            width: displayWidth,
            height: Math.round(
              (record.material.height * displayWidth) / record.material.width
            ),
            ...style,
          }}
          data-gd-click="resource_loc_click"
          data-gd-expose="resource_loc_expose"
          data-expose-time={mergeCol > 1 ? 1 : null}
          data-resource-type="图片"
          data-resource-id={record?.material?.id}
          data-resource-title={record?.promotion?.name}
          data-content-source={record?.position?.alias}
          data-resource-func={link?.includes("from=auto") ? "智能投放" : "广告"}
          data-click-type="点击广告"
          rel="noreferrer nofollow"
        >
          <When condition={record.showcase.closable}>
            <div
              className={styles.closeIcon}
              onClick={onClose}
              data-gd-click="resource_loc_click"
              data-click-type="关闭广告"
            >
              <HBIcon type="ic_close" />
            </div>
          </When>
          <img {...imgUrl} alt={record.material.tag} />
          <When condition={record.material.tag}>
            <div className={styles.labelTag}>{record.material.tag}</div>
          </When>
          <When condition={record.material.tracker}>
            <img
              src={record.material.tracker}
              alt="埋点"
              style={{ display: "none" }}
            />
          </When>
        </Link>
      </>
    );
  }
  return (
    <div
      data-gd-expose="resource_loc_expose"
      data-resource-type={"空广告"}
      data-resource-id={record?.material?.id}
      data-resource-title={record?.promotion?.name}
      data-content-source={record?.position?.alias}
    ></div>
  );
}
